import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Tag, Button, Drawer, notification, Space,
    Typography, Divider, Select, Tooltip, Input, Image,
} from 'antd';
import {
    EyeOutlined, CheckOutlined, CloseOutlined, ReloadOutlined,
    SearchOutlined, LoadingOutlined,
} from '@ant-design/icons';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';
import dayjs from 'dayjs';

const { Text } = Typography;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const docTypes = [
    { key: 'name_dob',         statusCol: 'name_dob_status',         proofCol: 'name_dob_proof',         label: 'Name / DOB' },
    { key: 'salary_workplace', statusCol: 'workplace_salary_status',  proofCol: 'workplace_salary_proof',  label: 'Workplace / Salary' },
    { key: 'education',        statusCol: 'education_status',         proofCol: 'education_proof',         label: 'Education' },
    { key: 'caste',            statusCol: 'caste_status',             proofCol: 'caste_proof',             label: 'Caste' },
    { key: 'religion',         statusCol: 'religion_status',         proofCol: 'religion_proof',          label: 'Religion' },
    { key: 'house',            statusCol: 'house_status',             proofCol: 'house_proof',             label: 'House (Encumbrance Certificate / House Tax Receipt)' },
    { key: 'car',              statusCol: 'car_status',               proofCol: 'car_proof',               label: 'Car (Registration Certificate / RC Book)' },
];

const statusTag = (v) => {
    if (v === 1) return <Tag color="green">Verified</Tag>;
    if (v === 0) return <Tag color="orange">Pending</Tag>;
    if (v === 3) return <Tag color="red">Rejected</Tag>;
    return <Tag color="default">N/A</Tag>;
};

export default function AdminVerifications() {
    const [rows, setRows]             = useState([]);
    const [loading, setLoading]       = useState(true);
    const [search, setSearch]         = useState('');
    const [perPage, setPerPage]       = useState(10);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [drawer, setDrawer]         = useState({ open: false, record: null, detail: null });
    const [detailLoading, setDetailLoading] = useState(false);
    const [updating, setUpdating]     = useState(null);

    const fetchList = useCallback(async (page = 1, searchVal = search, size = perPage) => {
        setLoading(true);
        try {
            const params = { page, per_page: size, pending_only: searchVal ? 0 : 1 };
            if (searchVal) params.search = searchVal;

            const res = await adminApi.get('/admin/verifications', { params });
            const d = res.data.data;
            setRows(d.data);
            setPagination({ current: d.current_page, pageSize: d.per_page, total: d.total });
        } catch (_) {
            notification.error({ message: 'Failed to Load', description: 'Could not fetch verification records. Please try again.' });
        } finally {
            setLoading(false);
        }
    }, [search, perPage]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchList(1); }, []);

    const handleSearch = (val) => {
        setSearch(val);
        fetchList(1, val);
    };

    const handlePerPageChange = (val) => {
        setPerPage(val);
        fetchList(1, search, val);
    };

    const openDrawer = async (record) => {
        setDrawer({ open: true, record, detail: null });
        setDetailLoading(true);
        try {
            const res = await adminApi.get(`/admin/verifications/${record.profile?.profile_id || record.profile?.id}`);
            setDrawer((prev) => ({ ...prev, detail: res.data.data }));
        } catch (_) {
            notification.error({ message: 'Failed to Load Detail', description: 'Could not fetch the verification documents. Please try again.' });
        } finally {
            setDetailLoading(false);
        }
    };

    const updateDocStatus = async (profileId, type, status) => {
        setUpdating(type);
        const docLabel = docTypes.find((d) => d.key === type)?.label || type;
        const isApproved = status === 1;
        try {
            await adminApi.patch(`/admin/verifications/${profileId}/${type}/status`, { status });
            notification[isApproved ? 'success' : 'error']({
                message: isApproved ? 'Document Approved' : 'Document Rejected',
                description: `${docLabel} has been ${isApproved ? 'approved and verified' : 'rejected'} successfully.`,
            });
            const res = await adminApi.get(`/admin/verifications/${profileId}`);
            setDrawer((prev) => ({ ...prev, detail: res.data.data }));
            fetchList(pagination.current);
        } catch (_) {
            notification.error({
                message: 'Update Failed',
                description: `Could not update the status for ${docLabel}. Please try again.`,
            });
        } finally {
            setUpdating(null);
        }
    };

    const countPending = (row) => docTypes.filter((d) => row[d.statusCol] === 0).length;

    const columns = [
        {
            title: 'Profile',
            render: (_, row) => (
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {row.profile?.first_name} {row.profile?.last_name}
                    </div>
                    {row.profile?.profile_id && (
                        <div style={{ fontSize: 11, color: 'var(--color-primary)', fontWeight: 600, marginTop: 2 }}>
                            {row.profile.profile_id}
                        </div>
                    )}
                    {row.profile?.user && (
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            {row.profile.user.email}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Pending Docs',
            width: 120,
            render: (_, row) => {
                const n = countPending(row);
                return n > 0
                    ? <Tag color="orange">{n} pending</Tag>
                    : <Tag color="green">All clear</Tag>;
            },
        },
        {
            title: 'Doc Status',
            render: (_, row) => (
                <Space wrap size={4}>
                    {docTypes.map((d) => (
                        <Tooltip key={d.key} title={d.label}>
                            {statusTag(row[d.statusCol])}
                        </Tooltip>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Updated',
            dataIndex: 'updated_at',
            width: 110,
            render: (v) => dayjs(v).format('D MMM YYYY'),
        },
        {
            title: 'Review',
            width: 90,
            render: (_, row) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => openDrawer(row)}
                    style={{ background: 'var(--gradient-primary)', border: 'none' }}
                >
                    Review
                </Button>
            ),
        },
    ];

    const detail = drawer.detail;

    return (
        <AdminLayout title="Verifications">
            <div className="admin-page-header">
                <h1 className="admin-page-title">Document Verifications</h1>
                <p className="admin-page-subtitle">Review and approve uploaded verification documents</p>
            </div>

            <div className="admin-card">
                <div className="admin-card-header">
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
                        <Input
                            placeholder="Search by profile ID or name..."
                            allowClear
                            prefix={<SearchOutlined style={{ color: 'var(--color-text-muted)' }} />}
                            value={search}
                            onChange={(e) => { if (!e.target.value) handleSearch(''); else setSearch(e.target.value); }}
                            onPressEnter={(e) => handleSearch(e.target.value)}
                            style={{ width: '100%', maxWidth: 280 }}
                        />
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={() => handleSearch(search)}
                            style={{ background: 'var(--gradient-primary)', border: 'none' }}
                        >
                            Search
                        </Button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Per page:</span>
                        <Select
                            value={perPage}
                            onChange={handlePerPageChange}
                            style={{ width: 80 }}
                            options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: n }))}
                        />
                        <Button icon={<ReloadOutlined />} onClick={() => fetchList(pagination.current)}>
                            Refresh
                        </Button>
                    </div>
                </div>

                <Table
                    className="admin-table"
                    dataSource={rows}
                    columns={columns}
                    rowKey="id"
                    loading={{ spinning: loading, indicator: <LoadingOutlined style={{ fontSize: 32, color: '#AD1457' }} spin /> }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        showQuickJumper: false,
                        showTotal: (t) => `${t} records`,
                        onChange: (page) => fetchList(page),
                    }}
                    scroll={{ x: 700 }}
                    size="middle"
                />
            </div>

            {/* Review Drawer */}
            <Drawer
                title={
                    detail
                        ? `${detail.profile?.first_name} ${detail.profile?.last_name} — ${detail.profile?.profile_id}`
                        : 'Loading...'
                }
                width={window.innerWidth < 768 ? '100%' : '70%'}
                open={drawer.open}
                onClose={() => setDrawer({ open: false, record: null, detail: null })}
                loading={detailLoading}
            >
                {detail && (
                    <div>
                        {detail.user && (
                            <div style={{ marginBottom: 16, padding: 12, background: 'var(--color-primary-faint)', borderRadius: 8 }}>
                                <Text strong>User: </Text>
                                <Text>{detail.user.email}</Text>
                                {detail.user.mobile_number && (
                                    <>
                                        <br />
                                        <Text strong>Phone: </Text>
                                        <Text>{detail.user.mobile_number}</Text>
                                    </>
                                )}
                            </div>
                        )}

                        <Divider orientation="left" style={{ fontSize: 13 }}>Document Status</Divider>

                        {(detail.documents || []).length === 0 && (
                            <Text style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                                No documents uploaded yet.
                            </Text>
                        )}

                        {(detail.documents || []).map((doc) => {
                            const { key, status, proof } = doc;
                            const docMeta   = docTypes.find((d) => d.key === key);
                            const profileId = detail.profile?.profile_id || detail.profile?.id;
                            const fileUrl   = `${process.env.REACT_APP_IMAGE_URL}/${proof}`;
                            const isImage   = /\.(jpg|jpeg|png)$/i.test(proof);
                            const isPdf     = /\.pdf$/i.test(proof);
                            const isPending = status === 0;

                            return (
                                <div key={key} style={{
                                    marginBottom: 16, padding: 14,
                                    border: `1px solid ${isPending ? '#F59E0B' : 'var(--color-border)'}`,
                                    borderRadius: 10, background: '#fff',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 14 }}>{docMeta?.label || key}</Text>
                                        {statusTag(status)}
                                    </div>

                                    <div style={{ marginBottom: isPending ? 10 : 0 }}>
                                        {isImage ? (
                                            <Image
                                                src={fileUrl}
                                                alt={docMeta?.label}
                                                style={{
                                                    width: '100%', maxHeight: 220,
                                                    objectFit: 'contain', borderRadius: 6,
                                                    border: '1px solid var(--color-border)',
                                                }}
                                            />
                                        ) : isPdf ? (
                                            <iframe
                                                src={fileUrl}
                                                title={docMeta?.label}
                                                style={{
                                                    width: '100%', height: 300,
                                                    border: '1px solid var(--color-border)',
                                                    borderRadius: 6,
                                                }}
                                            />
                                        ) : (
                                            <a href={fileUrl} target="_blank" rel="noreferrer"
                                                style={{ fontSize: 13, color: 'var(--color-primary)' }}>
                                                View Document ↗
                                            </a>
                                        )}
                                    </div>

                                    {isPending && (
                                        <Space style={{ marginTop: 10 }}>
                                            <Button
                                                size="small"
                                                icon={<CheckOutlined />}
                                                style={{ color: '#10B981', borderColor: '#10B981' }}
                                                loading={updating === key}
                                                onClick={() => updateDocStatus(profileId, key, 1)}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                size="small"
                                                icon={<CloseOutlined />}
                                                danger
                                                loading={updating === key}
                                                onClick={() => updateDocStatus(profileId, key, 3)}
                                            >
                                                Reject
                                            </Button>
                                        </Space>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Drawer>
        </AdminLayout>
    );
}
